import React, { useMemo, useState, useEffect } from "react"
import Select, { type StylesConfig, type GroupBase } from "react-select"
import { Input } from "reactstrap"

/** Internal sentinel; stored value is always the user's text when they pick Others. */
export const SOLDX_OTHER_OPTION_VALUE = "__soldx_other__"

export type SimpleSelectOption = { value: string; label: string }

export interface SelectWithOthersProps {
  options: SimpleSelectOption[]
  value: string
  onChange: (next: string) => void
  placeholder?: string
  classNamePrefix?: string
  isClearable?: boolean
  /** When false, same as plain react-select (no Others). Use for category/status that drive form shape. */
  allowOther?: boolean
  otherPlaceholder?: string
  otherInputClassName?: string
  /** Passed to react-select root (e.g. utility classes). */
  className?: string
  styles?: StylesConfig<SimpleSelectOption, false, GroupBase<SimpleSelectOption>>
}

export default function SelectWithOthers({
  options: baseOptions,
  value,
  onChange,
  placeholder = "Select",
  classNamePrefix = "select2-selection",
  isClearable = true,
  allowOther = true,
  otherPlaceholder = "Enter your value",
  otherInputClassName = "mt-2",
  className: selectClassName,
  styles: selectStyles,
}: SelectWithOthersProps) {
  const predefinedValues = useMemo(() => new Set(baseOptions.map((o) => o.value)), [baseOptions])

  /** True right after user picks "Others" while value is still "" — so the text input shows and dropdown stays on Others. */
  const [othersActive, setOthersActive] = useState(false)

  const optionsWithOther = useMemo(() => {
    if (!allowOther) return baseOptions
    const hasOthers = baseOptions.some(
      (o) => o.value === SOLDX_OTHER_OPTION_VALUE || String(o.label).trim().toLowerCase() === "others"
    )
    return hasOthers ? baseOptions : [...baseOptions, { value: SOLDX_OTHER_OPTION_VALUE, label: "Others" }]
  }, [baseOptions, allowOther])

  const isCustomValue = value !== "" && !predefinedValues.has(value)

  useEffect(() => {
    if (value !== "" && predefinedValues.has(value)) {
      setOthersActive(false)
    }
    if (isCustomValue) {
      setOthersActive(true)
    }
  }, [value, isCustomValue, predefinedValues])

  const selectedOption = useMemo(() => {
    if (!allowOther) {
      return optionsWithOther.find((o) => o.value === value) ?? null
    }
    if (isCustomValue) {
      return optionsWithOther.find((o) => o.value === SOLDX_OTHER_OPTION_VALUE) ?? null
    }
    if (othersActive && value === "") {
      return optionsWithOther.find((o) => o.value === SOLDX_OTHER_OPTION_VALUE) ?? null
    }
    return optionsWithOther.find((o) => o.value === value) ?? null
  }, [allowOther, optionsWithOther, value, isCustomValue, othersActive])

  const showOtherInput = allowOther && (isCustomValue || othersActive)

  if (!allowOther) {
    return (
      <Select
        className={selectClassName}
        placeholder={placeholder}
        value={selectedOption}
        onChange={(opt) => onChange(opt?.value ?? "")}
        options={optionsWithOther}
        classNamePrefix={classNamePrefix}
        isClearable={isClearable}
        styles={selectStyles}
      />
    )
  }

  return (
    <>
      <Select
        className={selectClassName}
        placeholder={placeholder}
        value={selectedOption}
        onChange={(opt) => {
          if (opt == null) {
            setOthersActive(false)
            onChange("")
            return
          }
          const v = String(opt.value ?? "")
          if (v === SOLDX_OTHER_OPTION_VALUE) {
            setOthersActive(true)
            const keep = predefinedValues.has(value) ? "" : value
            onChange(keep)
          } else {
            setOthersActive(false)
            onChange(v)
          }
        }}
        options={optionsWithOther}
        classNamePrefix={classNamePrefix}
        isClearable={isClearable}
        styles={selectStyles}
      />
      {showOtherInput && (
        <Input
          type="text"
          className={otherInputClassName}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={otherPlaceholder}
        />
      )}
    </>
  )
}
